import React, { useState, useEffect } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Box, Divider, Typography, Button, Grid } from "@mui/material";
import { BASE_INFOS_API, BASIC_INFORMATION_API } from "../../api";
import {
  updateBasicInformationData,
  setBasicInformationData,
  setIsLoading,
  setReloadBasicInformationData,
} from "../../slice";
import { useDate, useBasicInfo } from "../../hooks";
import BaseChecbox from "../all-inputs/BaseCheckbox";
import BaseTextArea from "../all-inputs/BaseTextArea";
import { TextInput, DatePicker, SelectBox, SwitchButton } from "../inputs";

function BasicInformation({
  hasEditAccess,
  onError = () => {},
  disabled = false,
  pageErrors = {},
}) {
  const dispatch = useDispatch();
  const { data, isNew, reload } = useSelector((state) => state.BasicInformation);
  const { userInfo } = useSelector((state) => state.UserCurrentState);
  const { simpleGregorianToPersian } = useDate();
  const [errors, setAllErrors] = useState({});
  const { handleSubmit: handleSubmitForm } = useBasicInfo({
    hasEditAccess,
    errors,
  });

  const handleLivingPlace = (event, key) => {
    if (key === "hasDorm") {
      if (event.target.checked) {
        dispatch(
          updateBasicInformationData({
            key: "livingAlone",
            value: true,
            required: true,
          })
        );
      }
      const residenceFields = [
        "residenceLandNumber",
        "residenceEmail",
        "residenceCountryId",
        "residenceProvinceId",
        "residenceCityId",
        "residenceRegionId",
        "residenceAddress",
        "residenceProvinceOther",
      ];
      residenceFields.forEach((key) => {
        dispatch(
          updateBasicInformationData({
            key,
            value: "",
            required: true,
          })
        );
      });
      dispatch(
        updateBasicInformationData({
          key,
          value: event.target.checked,
          required: true,
        })
      );
    }
    if (key === "livingAlone") {
      if (event.target.checked) {
        dispatch(
          updateBasicInformationData({
            key: "hasDorm",
            value: false,
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyLandNumber",
            value: data.residenceLandNumber !== undefined ? data.residenceLandNumber : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyCountryId",
            value: data.residenceCountryId !== undefined ? data.residenceCountryId : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyProvinceId",
            value: data.residenceProvinceId !== undefined ? data.residenceProvinceId : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyProvinceOther",
            value: data.residenceProvinceOther !== undefined ? data.residenceProvinceOther : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyCityId",
            value: data.residenceCityId !== undefined ? data.residenceCityId : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyRegionId",
            value: data.residenceRegionId !== undefined ? data.residenceRegionId : "",
            required: true,
          })
        );
        dispatch(
          updateBasicInformationData({
            key: "familyAddress",
            value: data.residenceAddress !== undefined ? data.residenceAddress : "",
            required: true,
          })
        );
      } else {
        const familyFields = [
          "familyMobileNumber",
          "familyLandNumber",
          "familyCountryId",
          "familyProvinceId",
          "familyCityId",
          "familyRegionId",
          "familyAddress",
          "familyProvinceOther",
        ];
        familyFields.forEach((key) => {
          dispatch(
            updateBasicInformationData({
              key,
              value: "",
              required: true,
            })
          );
        });
      }
      dispatch(
        updateBasicInformationData({
          key,
          value: !event.target.checked,
          required: true,
        })
      );
    }
  };

  const handleClearSelectedFields = (keys = []) => {
    keys.forEach((key) => {
      dispatch(
        updateBasicInformationData({
          key,
          value: "",
          required: true,
        })
      );
    });
  };

  const handleChangeLocationFields = (key, value, dependentFields = [], linkedFields = []) => {
    dispatch(updateBasicInformationData({ key, value, required: true }));
    handleClearSelectedFields(dependentFields);
    if (!data.livingAlone) {
      handleClearSelectedFields(linkedFields);
    }
  };

  const handleUpdateBasicInformations = ({ key, value, required = true }) =>
    dispatch(
      updateBasicInformationData({
        key,
        value,
        required,
      })
    );

  const { refetch: getBasicInformation } = useQuery(
    ["getBasicInformation"],
    () => BASIC_INFORMATION_API.GetBasicInformation(),
    {
      onSuccess: ({ data: basicInformation }) => {
        dispatch(setIsLoading(false));
        if (basicInformation.success && basicInformation.data) {
          let birthDate = "";
          if (basicInformation.data.birthDate) {
            birthDate = simpleGregorianToPersian(basicInformation.data.birthDate.split("T")[0]);
          }
          dispatch(
            setBasicInformationData({
              ...basicInformation.data,
              birthDate,
            })
          );
        }
        dispatch(setReloadBasicInformationData(false));
      },
      staleTime: Infinity,
    }
  );

  const isIranSelected = (countryId) => countryId === "1" || countryId === 1;

  const [
    countries,
    birthProvinces,
    residenceProvinces,
    familyProvinces,
    birthCities,
    residenceCities,
    familyCities,
    residenceRegions,
    familyRegions,
  ] = useQueries({
    queries: [
      {
        queryKey: ["getCountries"],
        queryFn: BASE_INFOS_API.GetAllCountries.bind(null, 1),
        staleTime: Infinity,
      },
      {
        queryKey: ["getProvincesByCountryId", parseInt(data.birthCountryId)],
        queryFn: BASE_INFOS_API.GetProvincesByCountryId.bind(null, data.birthCountryId),
        staleTime: Infinity,
        enabled: Boolean(data.birthCountryId) && isIranSelected(data.birthCountryId),
      },
      {
        queryKey: ["getProvincesByCountryId", parseInt(data.residenceCountryId)],
        queryFn: BASE_INFOS_API.GetProvincesByCountryId.bind(null, data.residenceCountryId),
        staleTime: Infinity,
        enabled: Boolean(data.residenceCountryId) && isIranSelected(data.residenceCountryId),
      },
      {
        queryKey: ["getProvincesByCountryId", parseInt(data.familyCountryId)],
        queryFn: BASE_INFOS_API.GetProvincesByCountryId.bind(null, data.familyCountryId),
        staleTime: Infinity,
        enabled: Boolean(data.familyCountryId) && isIranSelected(data.familyCountryId),
      },
      {
        queryKey: ["GetCitiesByProvinceId", parseInt(data.birthProvinceId)],
        queryFn: BASE_INFOS_API.GetCitiesByProvinceId.bind(null, data.birthProvinceId),
        staleTime: Infinity,
        enabled: Boolean(data.birthProvinceId),
      },
      {
        queryKey: ["GetCitiesByProvinceId", parseInt(data.residenceProvinceId)],
        queryFn: BASE_INFOS_API.GetCitiesByProvinceId.bind(null, data.residenceProvinceId),
        staleTime: Infinity,
        enabled: Boolean(data.residenceProvinceId),
      },
      {
        queryKey: ["GetCitiesByProvinceId", parseInt(data.familyProvinceId)],
        queryFn: BASE_INFOS_API.GetCitiesByProvinceId.bind(null, data.familyProvinceId),
        staleTime: Infinity,
        enabled: Boolean(data.familyProvinceId),
      },
      {
        queryKey: ["getRegionsByCityId", data.residenceCityId],
        queryFn: BASE_INFOS_API.GetRegionsByCityId.bind(null, data.residenceCityId),
        staleTime: Infinity,
        enabled: Boolean(data.residenceCityId),
      },
      {
        queryKey: ["getRegionsByCityId", data.familyCityId],
        queryFn: BASE_INFOS_API.GetRegionsByCityId.bind(null, data.familyCityId),
        staleTime: Infinity,
        enabled: Boolean(data.familyCityId),
      },
    ],
  });

  useEffect(() => {
    if (
      isNew &&
      userInfo.quickSignUpFirstName &&
      userInfo.quickSignUpLastName &&
      userInfo.quickSignUpPhoneNumber
    ) {
      dispatch(
        updateBasicInformationData({
          key: "firstName",
          value: userInfo.quickSignUpFirstName,
          required: true,
        })
      );
      dispatch(
        updateBasicInformationData({
          key: "lastName",
          value: userInfo.quickSignUpLastName,
          required: true,
        })
      );
      dispatch(
        updateBasicInformationData({
          key: "residenceMobileNumber",
          value: userInfo.quickSignUpPhoneNumber,
          required: true,
        })
      );
    }
  }, [dispatch, isNew, userInfo]);

  useEffect(() => {
    if (reload) getBasicInformation();
  }, [reload]);

  return (
    <form style={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
        <Typography variant="h1" mb="1.5rem">
          اطلاعات پایه
        </Typography>
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <TextInput
            placeholder="نام"
            label="نام"
            id="firstName"
            name="firstName"
            value={data.firstName}
            onChange={(event) =>
              handleUpdateBasicInformations({ key: "firstName", value: event.target.value })
            }
            validationType="text"
            disabled={disabled}
            error={"firstName" in pageErrors}
          />
          <TextInput
            placeholder=" نام خانوادگی"
            label=" نام خانوادگی"
            id="lastName"
            name="lastName"
            value={data.lastName}
            onChange={(event) =>
              handleUpdateBasicInformations({ key: "lastName", value: event.target.value })
            }
            disabled={disabled}
            error={"lastName" in pageErrors}
          />
          <TextInput
            placeholder="کد ملی"
            label="کد ملی"
            id="nationalCode"
            name="nationalCode"
            type="number"
            value={data.nationalCode}
            onChange={(event) => {
              handleUpdateBasicInformations({ key: "nationalCode", value: event.target.value });
            }}
            validationType="nationalCode"
            disabled={disabled}
            onErrorMessage={(error_message) => {
              setAllErrors({ ...errors, ...error_message });
              onError({ ...errors, ...error_message });
            }}
            error={"nationalCode" in pageErrors}
            reapetedValue={
              pageErrors["nationalCode"]?.match(/"(.*?)"/)
                ? pageErrors["nationalCode"]?.match(/"(.*?)"/)[1]
                : pageErrors["nationalCode"]
            }
          />
          <DatePicker
            label="تاریخ تولد"
            value={data.birthDate}
            onChange={(date) =>
              dispatch(
                updateBasicInformationData({
                  key: "birthDate",
                  value: date ? date.format() : "",
                  required: true,
                })
              )
            }
            id="birthDate"
            name="birthDate"
            placeholder="تاریخ تولد"
            disabled={disabled}
            error={"birthDate" in pageErrors}
          />
          <Grid item xs={12}>
            <BaseChecbox
              checked={data.isSayed}
              label="از خانواده سادات هستم"
              value="isSayed"
              style={{ marginRight: "0", float: "right", color: "#4C466F" }}
              onChange={(event) =>
                dispatch(
                  updateBasicInformationData({
                    key: "isSayed",
                    value: event.target.checked,
                    required: true,
                  })
                )
              }
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Divider sx={{ width: "100%", margin: "1.25rem 0 2rem 0" }} />
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Typography variant="h1" mb="1.5rem" width="100%">
            اطلاعات محل تولد
          </Typography>
          <SelectBox
            label="کشور"
            value={data.birthCountryId}
            id="birthCountryId"
            name="birthCountryId"
            data={countries?.data?.data.data}
            onChange={(event) =>
              handleChangeLocationFields("birthCountryId", event.target.value, [
                "birthProvinceId",
                "birthCityId",
                "birthProvinceOther",
              ])
            }
            disabled={disabled}
            error={"birthCountryId" in pageErrors}
          />
          {(!data.birthCountryId || isIranSelected(data.birthCountryId)) && (
            <SelectBox
              label="استان"
              value={data.birthProvinceId}
              id="birthProvinceId"
              name="birthProvinceId"
              data={birthProvinces?.data?.data.data}
              onChange={(event) =>
                handleChangeLocationFields("birthProvinceId", event.target.value, ["birthCityId"])
              }
              disabled={disabled}
              error={"birthProvinceId" in pageErrors}
            />
          )}
          {data.birthCountryId && !isIranSelected(data.birthCountryId) && (
            <TextInput
              placeholder="ایالت"
              label="ایالت"
              id="birthProvinceOther"
              value={data.birthProvinceOther}
              onChange={(event) =>
                handleUpdateBasicInformations({
                  key: "birthProvinceOther",
                  value: event.target.value,
                })
              }
              disabled={disabled}
              error={"birthProvinceOther" in pageErrors}
            />
          )}
          <SelectBox
            label="شهرستان"
            value={data.birthCityId}
            id="birthCityId"
            name="birthCityId"
            data={birthCities?.data?.data.data}
            onChange={(event) =>
              handleUpdateBasicInformations({ key: "birthCityId", value: event.target.value })
            }
            disabled={disabled || (data.birthCountryId && !isIranSelected(data.birthCountryId))}
            error={"birthCityId" in pageErrors}
          />
        </Grid>
        <Divider sx={{ width: "100%", margin: "1.25rem 0 2rem 0" }} />
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={7} md={5}>
            <Typography
              variant="h1"
              ml={{ xs: 0, sm: "0.8rem" }}
              mb={{ xs: "0.5rem", sm: 0 }}
              textAlign="right"
            >
              اطلاعات تماس و محل اقامت متقاضی
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5} md={5} mb="1.5rem">
            <SwitchButton
              checked={data.hasDorm}
              onChange={(event) => handleLivingPlace(event, "hasDorm")}
              label="در خوابگاه زندگی می کنم"
              disabled={disabled}
            />
          </Grid>

          <TextInput
            placeholder="تلفن همراه"
            label="تلفن همراه"
            type="number"
            id="residenceMobileNumber"
            value={data.residenceMobileNumber}
            disabled={true}
            validationType="phoneNumber"
            error={"residenceMobileNumber" in pageErrors}
          />
          <TextInput
            placeholder=" تلفن ثابت"
            label=" تلفن ثابت"
            type="number"
            id="residenceLandNumber"
            value={data.residenceLandNumber}
            onChange={(event) => {
              //Todo: bayad function shavad
              dispatch(
                updateBasicInformationData({
                  key: "residenceLandNumber",
                  value: event.target.value,
                  required: true,
                })
              );
              if (!data.livingAlone) {
                dispatch(
                  updateBasicInformationData({
                    key: "familyLandNumber",
                    value: event.target.value,
                    required: true,
                  })
                );
              }
            }}
            disabled={disabled}
            error={"residenceLandNumber" in pageErrors}
          />
          <TextInput
            label="ایمیل"
            id="residenceEmail"
            value={data.residenceEmail}
            containerGridSize={{ xs: 12 }}
            labelGridSize={{ xs: 5, sm: 2.45 }}
            inputGridSize={{ xs: 7, sm: 9.55 }}
            onChange={(event) =>
              handleUpdateBasicInformations({
                key: "residenceEmail",
                value: event.target.value,
              })
            }
            validationType="email"
            placeholder="hello@hello.com"
            disabled={disabled}
            onErrorMessage={(error_message) => {
              setAllErrors({ ...errors, ...error_message });
              onError({ ...errors, ...error_message });
            }}
            error={"residenceEmail" in pageErrors}
          />
          <SelectBox
            label="کشور"
            disabled={disabled}
            value={data.residenceCountryId}
            onChange={(event) => {
              //TODO: bayad function shavad
              handleChangeLocationFields(
                "residenceCountryId",
                event.target.value,
                [
                  "residenceProvinceId",
                  "residenceCityId",
                  "residenceRegionId",
                  "residenceProvinceOther",
                ],
                ["familyProvinceId", "familyCityId", "familyRegionId", "familyProvinceOther"]
              );
              if (!data.livingAlone) {
                dispatch(
                  updateBasicInformationData({
                    key: "familyCountryId",
                    value: event.target.value,
                    required: true,
                  })
                );
              }
            }}
            data={countries?.data?.data.data}
            error={"residenceCountryId" in pageErrors}
          />
          {(!data.residenceCountryId || isIranSelected(data.residenceCountryId)) && (
            <SelectBox
              label="استان"
              disabled={disabled}
              value={data.residenceProvinceId}
              onChange={(event) => {
                //TODO: bayad function shavad
                handleChangeLocationFields(
                  "residenceProvinceId",
                  event.target.value,
                  ["residenceCityId", "residenceRegionId"],
                  ["familyCityId", "familyRegionId"]
                );
                if (!data.livingAlone) {
                  dispatch(
                    updateBasicInformationData({
                      key: "familyProvinceId",
                      value: event.target.value,
                      required: true,
                    })
                  );
                }
              }}
              data={residenceProvinces?.data?.data.data}
              error={"residenceProvinceId" in pageErrors}
            />
          )}
          {data.residenceCountryId && !isIranSelected(data.residenceCountryId) && (
            <TextInput
              placeholder="ایالت"
              label="ایالت"
              id="residenceProvinceOther"
              value={data.residenceProvinceOther}
              onChange={(event) => {
                //TODO: bayad function shavad
                dispatch(
                  updateBasicInformationData({
                    key: "residenceProvinceOther",
                    value: event.target.value,
                    required: true,
                  })
                );
                if (!data.livingAlone) {
                  dispatch(
                    updateBasicInformationData({
                      key: "familyProvinceOther",
                      value: event.target.value,
                      required: true,
                    })
                  );
                }
              }}
              disabled={disabled}
              error={"residenceProvinceOther" in pageErrors}
            />
          )}
          <SelectBox
            label="شهرستان"
            value={data.residenceCityId}
            onChange={(event) => {
              //TODO: bayad function shavad
              handleChangeLocationFields(
                "residenceCityId",
                event.target.value,
                ["residenceRegionId"],
                ["familyRegionId"]
              );
              if (!data.livingAlone) {
                dispatch(
                  updateBasicInformationData({
                    key: "familyCityId",
                    value: event.target.value,
                    required: true,
                  })
                );
              }
            }}
            data={residenceCities?.data?.data.data}
            disabled={
              disabled || (data.residenceCountryId && !isIranSelected(data.residenceCountryId))
            }
            error={"residenceCityId" in pageErrors}
          />
          <SelectBox
            label="منطقه"
            value={data.residenceRegionId}
            onChange={(event) => {
              //TODO: bayad function shavd
              if (!data.livingAlone) {
                dispatch(
                  updateBasicInformationData({
                    key: "familyRegionId",
                    value: event.target.value,
                    required: true,
                  })
                );
              }
              dispatch(
                updateBasicInformationData({
                  key: "residenceRegionId",
                  value: event.target.value,
                  required: true,
                })
              );
            }}
            data={residenceRegions?.data?.data.data}
            disabled={
              disabled || (data.residenceCountryId && !isIranSelected(data.residenceCountryId))
            }
            error={"residenceRegionId" in pageErrors}
          />
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="نشانی"
              placeholder="نشانی"
              flex={{ xs: 6.7, sm: 9.7, md: 9.6, lg: 9.6 }}
              labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
              disabled={disabled}
              value={data.residenceAddress}
              onChange={(event) => {
                if (!data.livingAlone) {
                  dispatch(
                    updateBasicInformationData({
                      key: "familyAddress",
                      value: event.target.value,
                      required: true,
                    })
                  );
                }
                dispatch(
                  updateBasicInformationData({
                    key: "residenceAddress",
                    value: event.target.value,
                    required: true,
                  })
                );
              }}
              error={"residenceAddress" in pageErrors}
            />
          </Grid>
        </Grid>
        <Divider sx={{ width: "100%", margin: "1.25rem 0 2rem 0" }} />
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={7} md={5}>
            <Typography
              variant="h1"
              ml={{ xs: 0, sm: "0.8rem" }}
              mb={{ xs: "0.5rem", sm: 0 }}
              textAlign="right"
            >
              اطلاعات تماس و محل اقامت خانواده
            </Typography>
          </Grid>
          <Grid item xs={12} sm={5} md={5} mb="1.5rem">
            <SwitchButton
              label="با خانواده خود زندگی می کنم"
              checked={!data.livingAlone}
              onChange={(event) => handleLivingPlace(event, "livingAlone")}
              disabled={disabled}
            />
          </Grid>
          <TextInput
            placeholder="تلفن همراه"
            label="تلفن همراه"
            type="number"
            id="familyMobileNumber"
            value={data.familyMobileNumber}
            onChange={(event) => {
              handleUpdateBasicInformations({
                key: "familyMobileNumber",
                value: event.target.value,
              });
            }}
            validationType="phoneNumber"
            onErrorMessage={(error_message) => {
              setAllErrors({ ...errors, ...error_message });
              onError({ ...errors, ...error_message });
            }}
            disabled={disabled}
            error={"familyMobileNumber" in pageErrors}
          />
          <TextInput
            placeholder=" تلفن ثابت"
            label=" تلفن ثابت"
            type="number"
            id="familyLandNumber"
            value={data.familyLandNumber}
            onChange={(event) =>
              handleUpdateBasicInformations({
                key: "familyLandNumber",
                value: event.target.value,
              })
            }
            disabled={disabled}
            error={"familyLandNumber" in pageErrors}
          />
          <SelectBox
            label="کشور"
            value={data.familyCountryId}
            onChange={(event) =>
              handleChangeLocationFields("familyCountryId", event.target.value, [
                "familyProvinceId",
                "familyCityId",
                "familyRegionId",
                "familyProvinceOther",
              ])
            }
            data={countries?.data?.data.data}
            disabled={!data.livingAlone || disabled}
            error={"familyCountryId" in pageErrors}
          />
          {(!data.familyCountryId || isIranSelected(data.familyCountryId)) && (
            <SelectBox
              label="استان"
              value={data.familyProvinceId}
              onChange={(event) =>
                handleChangeLocationFields("familyProvinceId", event.target.value, [
                  "familyCityId",
                  "familyRegionId",
                ])
              }
              data={familyProvinces?.data?.data.data}
              disabled={!data.livingAlone || disabled}
              error={"familyProvinceId" in pageErrors}
            />
          )}
          {data.familyCountryId && !isIranSelected(data.familyCountryId) && (
            <TextInput
              placeholder="ایالت"
              label="ایالت"
              id="familyProvinceOther"
              value={data.familyProvinceOther}
              onChange={(event) => {
                handleUpdateBasicInformations({
                  key: "familyProvinceOther",
                  value: event.target.value,
                });
              }}
              disabled={!data.livingAlone || disabled}
              error={"familyProvinceOther" in pageErrors}
            />
          )}
          <SelectBox
            label="شهرستان"
            value={data.familyCityId}
            onChange={(event) =>
              handleChangeLocationFields("familyCityId", event.target.value, ["familyRegionId"])
            }
            data={familyCities?.data?.data.data}
            disabled={
              !data.livingAlone ||
              disabled ||
              (data.familyCountryId && !isIranSelected(data.familyCountryId))
            }
            error={"familyCityId" in pageErrors}
          />
          <SelectBox
            label="منطقه"
            value={data.familyRegionId}
            onChange={(event) =>
              handleUpdateBasicInformations({
                key: "familyRegionId",
                value: event.target.value,
              })
            }
            data={familyRegions?.data?.data.data}
            disabled={
              !data.livingAlone ||
              disabled ||
              (data.familyCountryId && !isIranSelected(data.familyCountryId))
            }
            error={"familyRegionId" in pageErrors}
          />
          <Grid item xs={12} sm={12} md={12} lg={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="نشانی"
              placeholder="نشانی"
              flex={{ xs: 6.7, sm: 9.7, md: 9.65, lg: 9.65 }}
              labelFlex={{ xs: 5, sm: 2.5, md: 2.5, lg: 2.5 }}
              disabled={!data.livingAlone || disabled}
              value={data.familyAddress}
              name="familyAddress"
              onChange={(event) =>
                dispatch(
                  updateBasicInformationData({
                    key: "familyAddress",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              error={"familyAddress" in pageErrors}
            />
          </Grid>
        </Grid>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            mt: "2rem",
          }}
        >
          <Button
            type="button"
            variant="m1"
            onClick={() => handleSubmitForm("/personal-info/personal-info")}
            sx={{ mr: 1, width: "165px", my: { xs: 1, sm: 0, md: 0 } }}
          >
            بعدی
          </Button>
        </Box>
      </Box>
    </form>
  );
}

const Style = {
  itemGrid: {
    boxSizing: "border-box",
    paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem", lg: "1rem" },
    marginBottom: "0.75rem",
  },
};

export default BasicInformation;
